import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Monitor, Smartphone, Square, Instagram, Youtube, Eye, EyeOff,
  Plus, Settings, Save, Upload, Download, X, Edit3,
  Image, Frame, Layers, Palette, Sliders, Copy, Target,
  Trash2, ArrowUp, ArrowDown, Type, Video
} from 'lucide-react';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  
  /* Face Tracker Animation */
  @keyframes facePulse {
    0% { 
      transform: translateX(-50%) scale(1);
      opacity: 0.8;
    }
    50% { 
      transform: translateX(-50%) scale(1.1);
      opacity: 1;
    }
    100% { 
      transform: translateX(-50%) scale(1);
      opacity: 0.8;
    }
  }
  
  /* Face Tracker Animation */
  @keyframes facePulse {
    0% { 
      transform: translateX(-50%) scale(1);
      opacity: 0.8;
    }
    50% { 
      transform: translateX(-50%) scale(1.1);
      opacity: 1;
    }
    100% { 
      transform: translateX(-50%) scale(1);
      opacity: 0.8;
    }
  }
`;

const Section = styled.div`
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.background};
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
`;

const FormatOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border: 2px solid ${props => props.selected ? props.theme.primary : props.theme.border};
  border-radius: 8px;
  background: ${props => props.selected ? props.theme.primary + '10' : props.theme.surface};
  color: ${props => props.theme.text};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    transform: translateY(-2px);
  }
`;

const FormatIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.selected ? props.theme.primary : props.theme.textSecondary};
`;

const FormatLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-align: center;
`;

const FormatRatio = styled.div`
  font-size: 10px;
  color: ${props => props.theme.textSecondary};
`;

// Custom Format Inputs
const CustomFormatSection = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px dashed ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
`;

const RatioInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const RatioInput = styled.input`
  width: 60px;
  padding: 6px 8px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-size: 14px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const RatioLabel = styled.span`
  font-size: 14px;
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const PresetContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const PresetButton = styled.button`
  padding: 4px 8px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.primary + '20'};
    border-color: ${props => props.theme.primary};
  }
`;

// Advanced Customization
const AdvancedSection = styled.div`
  margin-top: 16px;
`;

const CustomizeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
`;

const CustomizeOption = styled.div`
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
`;

const CustomizeTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FileUploadButton = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px dashed ${props => props.theme.border};
  border-radius: 4px;
  background: transparent;
  color: ${props => props.theme.textSecondary};
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    color: ${props => props.theme.primary};
  }
`;

const ToggleSwitch = styled.button`
  width: 40px;
  height: 20px;
  border-radius: 10px;
  border: none;
  background: ${props => props.active ? props.theme.primary : props.theme.border};
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: ${props => props.active ? '22px' : '2px'};
    transition: all 0.2s ease;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${props => props.theme.text};
`;

const SliderContainer = styled.div`
  margin-top: 8px;
`;

const SliderLabel = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 4px;
`;

const Slider = styled.input`
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: ${props => props.theme.border};
  outline: none;
  appearance: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.primary};
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.primary};
    cursor: pointer;
    border: none;
  }
`;

// Preview
const PreviewContainer = styled.div`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.surface};
`;

const PreviewTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PreviewFrame = styled.div`
  width: 100%;
  max-width: 200px;
  aspect-ratio: ${props => props.ratio};
  border: 2px solid ${props => props.theme.border};
  border-radius: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 500;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
`;

const FrameOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  
  ${props => props.frameType === 'border' && `
    border: ${props.frameSize}px solid ${props.frameColor};
  `}
  
  ${props => props.frameType === 'shadow' && `
    box-shadow: inset 0 0 ${props.frameSize}px ${props.frameColor};
  `}
`;

// Preset Management
const PresetManagement = styled.div`
  margin-top: 16px;
`;

const PresetActions = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.variant === 'primary' ? props.theme.primary : 'transparent'};
  color: ${props => props.variant === 'primary' ? 'white' : props.theme.text};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? props.theme.primary + 'dd' : props.theme.surface};
    transform: translateY(-1px);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const SavedPresetsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 150px;
  overflow-y: auto;
`;

const PresetItem = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.surface};
`;

const PresetName = styled.span`
  font-size: 12px;
  color: ${props => props.theme.text};
  flex: 1;
`;

const PresetInfo = styled.span`
  font-size: 10px;
  color: ${props => props.theme.textSecondary};
  margin-left: 8px;
`;

const PresetActions2 = styled.div`
  display: flex;
  gap: 4px;
  margin-left: 8px;
`;

const SmallButton = styled.button`
  padding: 4px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: transparent;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.surface};
    color: ${props => props.theme.text};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

// Modal Components
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.background};
  padding: 24px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.border};
  min-width: 300px;
  max-width: 90vw;
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  color: ${props => props.theme.text};
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

// Layers styled components
const CanvasContainer = styled.div`
  width: 100%;
  max-width: 280px;
  aspect-ratio: ${props => props.ratio};
  border: 2px solid ${props => props.theme.border};
  border-radius: 4px;
  background: #111;
  position: relative;
  overflow: hidden;
  margin: 0 auto;
  user-select: none;
`;

const LayerWrapper = styled.div`
  position: absolute;
  top: ${props => props.y}%;
  left: ${props => props.x}%;
  width: ${props => props.w}%;
  height: ${props => props.h}%;
  z-index: ${props => props.zIndex};
  border: ${props => props.active ? '2px solid ' + props.theme.primary : '1px dashed rgba(255,255,255,0.3)'};
  cursor: ${props => props.active ? 'move' : 'pointer'};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ResizeHandle = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  border: 2px solid ${props => props.theme.primary};
  border-radius: 50%;
  z-index: 10;
  
  ${props => props.position === 'top-left' && 'top: -5px; left: -5px; cursor: nwse-resize;'}
  ${props => props.position === 'top-right' && 'top: -5px; right: -5px; cursor: nesw-resize;'}
  ${props => props.position === 'bottom-left' && 'bottom: -5px; left: -5px; cursor: nesw-resize;'}
  ${props => props.position === 'bottom-right' && 'bottom: -5px; right: -5px; cursor: nwse-resize;'}
`;

const LayerItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 6px;
  background: ${props => props.active ? props.theme.primary + '10' : props.theme.surface};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 6px;

  &:hover {
    border-color: ${props => props.theme.primary};
  }
`;

const LayerNameLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormatCropping = ({ project, onUpdate }) => {
  const [selectedFormat, setSelectedFormat] = useState(project.settings?.format || '16:9');
  const [canvasFormat, setCanvasFormat] = useState(project.settings?.customization?.canvasFormat || '9:16');
  const [customWidth, setCustomWidth] = useState(project.settings?.customWidth?.toString() || '16');
  const [customHeight, setCustomHeight] = useState(project.settings?.customHeight?.toString() || '9');
  const [showCustom, setShowCustom] = useState(
    project.settings?.format === 'custom' || 
    (project.settings?.format === 'advanced' && project.settings?.customization?.canvasFormat === 'custom')
  );

  // Sync canvasFormat on customization change
  useEffect(() => {
    if (project.settings?.customization?.canvasFormat) {
      setCanvasFormat(project.settings.customization.canvasFormat);
    }
  }, [project.settings?.customization?.canvasFormat]);

  // Sync format and crop align settings when updated elsewhere
  useEffect(() => {
    if (project.settings) {
      if (project.settings.format) setSelectedFormat(project.settings.format);
      if (project.settings.manualCropX !== undefined) setManualCropX(project.settings.manualCropX);
      if (project.settings.manualCropY !== undefined) setManualCropY(project.settings.manualCropY);
    }
  }, [project.settings?.format, project.settings?.manualCropX, project.settings?.manualCropY]);

  // Sync advanced customization properties when they change in project
  useEffect(() => {
    if (project.settings?.customization) {
      const cust = project.settings.customization;
      if (cust.canvasFormat) setCanvasFormat(cust.canvasFormat);
      if (cust.layers) setLayers(cust.layers);
      if (cust.frameImage !== undefined) setFrameImage(cust.frameImage);
      if (cust.frameType !== undefined) setFrameType(cust.frameType);
      if (cust.frameSize !== undefined) setFrameSize(cust.frameSize);
      if (cust.frameColor !== undefined) setFrameColor(cust.frameColor);
      if (cust.logoImage !== undefined) setLogoImage(cust.logoImage);
      if (cust.logoPosition !== undefined) setLogoPosition(cust.logoPosition);
      if (cust.logoSize !== undefined) setLogoSize(cust.logoSize);
      if (cust.logoOpacity !== undefined) setLogoOpacity(cust.logoOpacity);
      if (cust.faceTrackingEnabled !== undefined) setFaceTrackingEnabled(cust.faceTrackingEnabled);
      if (cust.faceTrackingSubMode !== undefined) setFaceTrackingSubMode(cust.faceTrackingSubMode);
      if (cust.faceTrackingMode !== undefined) setFaceTrackingMode(cust.faceTrackingMode);
      if (cust.faceTrackingSensitivity !== undefined) setFaceTrackingSensitivity(cust.faceTrackingSensitivity);
      if (cust.facePadding !== undefined) setFacePadding(cust.facePadding);
    }
  }, [project.settings?.customization]);
  
  // Advanced customization states
  const [frameImage, setFrameImage] = useState(project.settings?.customization?.frameImage || null);
  const [frameType, setFrameType] = useState(project.settings?.customization?.frameType || 'none');
  const [frameSize, setFrameSize] = useState(project.settings?.customization?.frameSize ?? 10);
  const [frameColor, setFrameColor] = useState(project.settings?.customization?.frameColor || '#000000');
  const [logoImage, setLogoImage] = useState(project.settings?.customization?.logoImage || null);
  const [logoPosition, setLogoPosition] = useState(project.settings?.customization?.logoPosition || 'bottom-right');
  const [logoSize, setLogoSize] = useState(project.settings?.customization?.logoSize ?? 50);
  const [logoOpacity, setLogoOpacity] = useState(project.settings?.customization?.logoOpacity ?? 80);
  
  // Face tracking states
  const [faceTrackingEnabled, setFaceTrackingEnabled] = useState(project.settings?.customization?.faceTrackingEnabled || false);
  const [faceTrackingSubMode, setFaceTrackingSubMode] = useState(project.settings?.customization?.faceTrackingSubMode || 'automatic');
  const [faceTrackingMode, setFaceTrackingMode] = useState(project.settings?.customization?.faceTrackingMode || 'center'); // center, follow, smart
  const [faceTrackingSensitivity, setFaceTrackingSensitivity] = useState(project.settings?.customization?.faceTrackingSensitivity || 50);
  const [facePadding, setFacePadding] = useState(project.settings?.customization?.facePadding || 20); // padding around detected face
  
  // Layers default definition and state
  const DEFAULT_LAYERS = [
    { id: 'bg', type: 'background', color: '#000000', imagePath: null, zIndex: 0 },
    { id: 'vid', type: 'video', x: 0, y: 25, w: 100, h: 50, cropW: 100, cropH: 100, faceTracking: false, zIndex: 1 },
    { id: 'txt', type: 'text', x: 10, y: 80, w: 80, h: 10, content: '{part_name}', style: { fontSize: 24, color: '#ffffff', fontFamily: 'Arial', fontWeight: 'bold', textAlign: 'center' }, zIndex: 2 }
  ];

  const [layers, setLayers] = useState(project.settings?.customization?.layers || DEFAULT_LAYERS);
  const [selectedLayerId, setSelectedLayerId] = useState(null);

  const handleLayerMouseDown = (e, layerId) => {
    if (layerId === 'bg') return; // prevent background drag
    e.stopPropagation();
    setSelectedLayerId(layerId);
    
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startLayerX = layer.x;
    const startLayerY = layer.y;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      // Get dimensions of the preview frame to convert pixels to percentages
      const frameEl = document.getElementById('canvas-preview-frame');
      if (!frameEl) return;
      const rect = frameEl.getBoundingClientRect();

      const pctX = (deltaX / rect.width) * 100;
      const pctY = (deltaY / rect.height) * 100;

      // New positions
      let newX = Math.round(startLayerX + pctX);
      let newY = Math.round(startLayerY + pctY);
      
      // Clamp coordinates
      newX = Math.max(-400, Math.min(400, newX));
      newY = Math.max(-400, Math.min(400, newY));

      const updated = layers.map(l => l.id === layerId ? { ...l, x: newX, y: newY } : l);
      setLayers(updated);
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      // Auto-save settings
      updateAdvancedSettings(layers);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleResizeMouseDown = (e, layerId, position) => {
    e.stopPropagation();
    const layer = layers.find(l => l.id === layerId);
    if (!layer) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startLayerX = layer.x;
    const startLayerY = layer.y;
    const startW = layer.w;
    const startH = layer.h;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const frameEl = document.getElementById('canvas-preview-frame');
      if (!frameEl) return;
      const rect = frameEl.getBoundingClientRect();

      const pctX = (deltaX / rect.width) * 100;
      const pctY = (deltaY / rect.height) * 100;

      let newX = startLayerX;
      let newY = startLayerY;
      let newW = startW;
      let newH = startH;

      if (position === 'bottom-right') {
        newW = Math.round(startW + pctX);
        newH = Math.round(startH + pctY);
      } else if (position === 'bottom-left') {
        newX = Math.round(startLayerX + pctX);
        newW = Math.round(startW - pctX);
        newH = Math.round(startH + pctY);
      } else if (position === 'top-right') {
        newY = Math.round(startLayerY + pctY);
        newW = Math.round(startW + pctX);
        newH = Math.round(startH - pctY);
      } else if (position === 'top-left') {
        newX = Math.round(startLayerX + pctX);
        newY = Math.round(startLayerY + pctY);
        newW = Math.round(startW - pctX);
        newH = Math.round(startH - pctY);
      }

      // Clamp size to minimum 5%
      newW = Math.max(5, Math.min(500, newW));
      newH = Math.max(5, Math.min(500, newH));

      const updated = layers.map(l => l.id === layerId ? { ...l, x: newX, y: newY, w: newW, h: newH } : l);
      setLayers(updated);
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      // Auto-save settings
      updateAdvancedSettings(layers);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };
  
  // Manual crop positioning states
  const [manualCropX, setManualCropX] = useState(project.settings?.manualCropX ?? 50);
  const [manualCropY, setManualCropY] = useState(project.settings?.manualCropY ?? 50);
  
  // Preset management
  const [savedPresets, setSavedPresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');

  const formats = [
    {
      id: '9:16',
      label: 'Shorts / Reels / TikTok',
      ratio: '9/16',
      icon: Smartphone,
      description: 'Portrait (1080x1920)'
    },
    {
      id: '16:9',
      label: 'YouTube / Landscape',
      ratio: '16/9',
      icon: Youtube,
      description: 'Landscape (1920x1080)'
    },
    {
      id: '1:1',
      label: 'Instagram Square',
      ratio: '1/1',
      icon: Square,
      description: 'Feed (1080x1080)'
    },
    {
      id: '4:5',
      label: 'Instagram Portrait',
      ratio: '4/5',
      icon: Instagram,
      description: 'Feed (1080x1350)'
    },
    {
      id: 'custom',
      label: 'Custom Size',
      ratio: `${customWidth}/${customHeight}`,
      icon: Settings,
      description: `Custom (${customWidth}:${customHeight})`
    },
    {
      id: 'advanced',
      label: 'Customize',
      ratio: '16/9',
      icon: Palette,
      description: 'Advanced editing'
    }
  ];

  const presetRatios = [
    { label: '3:2', width: '3', height: '2' },
    { label: '5:4', width: '5', height: '4' },
    { label: '21:9', width: '21', height: '9' },
    { label: '2:1', width: '2', height: '1' },
    { label: '100:90', width: '100', height: '90' },
    { label: '80:60', width: '80', height: '60' }
  ];

  // Load saved presets on component mount
  useEffect(() => {
    const loadPresets = () => {
      try {
        const saved = localStorage.getItem('formatPresets');
        if (saved) {
          setSavedPresets(JSON.parse(saved));
        }
      } catch (error) {
        console.error('Error loading presets:', error);
      }
    };
    loadPresets();
  }, []);

  const updateCropSettings = (updates = {}) => {
    const isFtEnabled = updates.faceTrackingEnabled !== undefined ? updates.faceTrackingEnabled : faceTrackingEnabled;
    const ftSubMode = updates.faceTrackingSubMode !== undefined ? updates.faceTrackingSubMode : faceTrackingSubMode;
    const ftMode = updates.faceTrackingMode !== undefined ? updates.faceTrackingMode : faceTrackingMode;
    const ftSens = updates.faceTrackingSensitivity !== undefined ? updates.faceTrackingSensitivity : faceTrackingSensitivity;
    const ftPad = updates.facePadding !== undefined ? updates.facePadding : facePadding;
    const mcX = updates.manualCropX !== undefined ? updates.manualCropX : manualCropX;
    const mcY = updates.manualCropY !== undefined ? updates.manualCropY : manualCropY;
    const fmt = updates.format !== undefined ? updates.format : selectedFormat;
    const customW = updates.customWidth !== undefined ? updates.customWidth : customWidth;
    const customH = updates.customHeight !== undefined ? updates.customHeight : customHeight;
    const updatedLayers = updates.layers !== undefined ? updates.layers : layers;
    
    const activeCanvasFmt = updates.canvasFormat !== undefined ? updates.canvasFormat : canvasFormat;
    
    let ratio = getAspectRatio();
    if (updates.aspectRatio) {
      ratio = updates.aspectRatio;
    } else if (fmt === 'custom') {
      ratio = `${customW}/${customH}`;
    } else if (fmt === 'advanced') {
      if (activeCanvasFmt === 'custom') {
        ratio = `${customW}/${customH}`;
      } else {
        const format = formats.find(f => f.id === activeCanvasFmt);
        ratio = format ? format.ratio : '9/16';
      }
    }

    // Keep the customization object populated in project settings even when standard formats are active,
    // so returning to "Customize" mode preserves all layers, frames, logos, and target aspect ratios.
    const customization = {
      canvasFormat: activeCanvasFmt,
      frameImage,
      frameType,
      frameSize,
      frameColor,
      logoImage,
      logoPosition,
      logoSize,
      logoOpacity,
      faceTrackingEnabled: isFtEnabled,
      faceTrackingSubMode: ftSubMode,
      faceTrackingMode: ftMode,
      faceTrackingSensitivity: ftSens,
      facePadding: ftPad,
      layers: updatedLayers
    };

    onUpdate({
      settings: {
        ...project.settings,
        format: fmt,
        aspectRatio: ratio,
        customWidth: parseInt(customW),
        customHeight: parseInt(customH),
        customization,
        manualCropX: mcX,
        manualCropY: mcY
      }
    });
  };

  const handleFormatChange = (formatId) => {
    setSelectedFormat(formatId);
    const isCustomActive = formatId === 'custom' || (formatId === 'advanced' && canvasFormat === 'custom');
    setShowCustom(isCustomActive);
    
    let ratio;
    if (formatId === 'custom') {
      ratio = `${customWidth}/${customHeight}`;
    } else if (formatId === 'advanced') {
      if (canvasFormat === 'custom') {
        ratio = `${customWidth}/${customHeight}`;
      } else {
        const format = formats.find(f => f.id === canvasFormat);
        ratio = format ? format.ratio : '9/16';
      }
    } else {
      const format = formats.find(f => f.id === formatId);
      ratio = format ? format.ratio : '16/9';
    }
    
    updateCropSettings({ 
      format: formatId, 
      aspectRatio: ratio,
      canvasFormat: canvasFormat
    });
  };

  const handleCustomRatioChange = () => {
    updateCropSettings({ 
      format: 'custom'
    });
  };

  const handlePresetRatio = (preset) => {
    setCustomWidth(preset.width);
    setCustomHeight(preset.height);
    updateCropSettings({ 
      format: 'custom', 
      customWidth: preset.width,
      customHeight: preset.height
    });
  };

  const handleFrameUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFrameImage(e.target.result);
        updateAdvancedSettings();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoImage(e.target.result);
        updateAdvancedSettings();
      };
      reader.readAsDataURL(file);
    }
  };

  const updateAdvancedSettings = (updatedLayers = layers) => {
    updateCropSettings({
      layers: updatedLayers
    });
  };

  const addTextLayer = () => {
    const newId = `txt_${Date.now()}`;
    const newLayer = {
      id: newId,
      type: 'text',
      x: 10,
      y: 75,
      w: 80,
      h: 15,
      content: 'New Text Layer',
      style: {
        fontSize: 28,
        color: '#ffffff',
        fontFamily: 'Arial',
        fontWeight: 'bold',
        textAlign: 'center'
      },
      zIndex: layers.length
    };
    const updated = [...layers, newLayer];
    setLayers(updated);
    setSelectedLayerId(newId);
    updateAdvancedSettings(updated);
  };

  const addLogoLayer = () => {
    const newId = `logo_${Date.now()}`;
    const newLayer = {
      id: newId,
      type: 'logo',
      x: 70,
      y: 10,
      w: 20,
      h: 10,
      imagePath: null,
      opacity: 80,
      zIndex: layers.length
    };
    const updated = [...layers, newLayer];
    setLayers(updated);
    setSelectedLayerId(newId);
    updateAdvancedSettings(updated);
  };

  const deleteLayer = (id) => {
    if (id === 'bg' || id === 'vid') return; // Cannot delete core layers
    const updated = layers.filter(l => l.id !== id);
    // Re-index zIndex
    const reindexed = updated.map((l, i) => ({ ...l, zIndex: i }));
    setLayers(reindexed);
    setSelectedLayerId(null);
    updateAdvancedSettings(reindexed);
  };

  const toggleLayerVisibility = (id) => {
    const updated = layers.map(l => {
      if (l.id === id) {
        return { ...l, visible: l.visible === false ? true : false };
      }
      return l;
    });
    setLayers(updated);
    updateAdvancedSettings(updated);
  };

  const moveLayerDepth = (id, direction) => {
    const index = layers.findIndex(l => l.id === id);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index + 1 : index - 1;
    if (newIndex < 0 || newIndex >= layers.length) return;

    const updated = [...layers];
    // Swap zIndex values
    const tempZ = updated[index].zIndex;
    updated[index].zIndex = updated[newIndex].zIndex;
    updated[newIndex].zIndex = tempZ;

    // Sort by zIndex to keep array index aligned
    updated.sort((a, b) => a.zIndex - b.zIndex);

    setLayers(updated);
    updateAdvancedSettings(updated);
  };

  const updateSelectedLayerProperty = (key, value) => {
    if (!selectedLayerId) return;
    const updated = layers.map(l => {
      if (l.id === selectedLayerId) {
        if (key.includes('.')) {
          const [parentKey, childKey] = key.split('.');
          return {
            ...l,
            [parentKey]: {
              ...l[parentKey],
              [childKey]: value
            }
          };
        }
        return { ...l, [key]: value };
      }
      return l;
    });
    setLayers(updated);
    updateAdvancedSettings(updated);
  };

  const savePreset = () => {
    if (!presetName.trim()) return;
    
    // Create customization object for Face Tracker and/or Advanced features
    let customization = null;
    
    if (selectedFormat === 'advanced') {
      customization = {
        canvasFormat,
        faceTrackingEnabled,
        faceTrackingSubMode,
        faceTrackingMode,
        faceTrackingSensitivity,
        facePadding,
        frameImage,
        frameType,
        frameSize,
        frameColor,
        logoImage,
        logoPosition,
        logoSize,
        logoOpacity,
        layers: layers // include layers
      };
    } else if (selectedFormat && selectedFormat !== 'original' && faceTrackingEnabled) {
      customization = {
        faceTrackingEnabled: true,
        faceTrackingSubMode,
        faceTrackingMode,
        faceTrackingSensitivity,
        facePadding
      };
    }
    
    const preset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      format: selectedFormat,
      aspectRatio: getAspectRatio(),
      customWidth: parseInt(customWidth),
      customHeight: parseInt(customHeight),
      customization,
      manualCropX,
      manualCropY,
      createdAt: new Date().toISOString()
    };

    const updated = [...savedPresets, preset];
    setSavedPresets(updated);
    localStorage.setItem('formatPresets', JSON.stringify(updated));
    setShowSaveModal(false);
    setPresetName('');
  };

  const loadPreset = (preset) => {
    setSelectedFormat(preset.format);
    setCustomWidth(preset.customWidth?.toString() || '16');
    setCustomHeight(preset.customHeight?.toString() || '9');
    setShowCustom(preset.format === 'custom');
    
    // Reset Face Tracker settings first
    setFaceTrackingEnabled(false);
    setFaceTrackingSubMode('automatic');
    setFaceTrackingMode('center');
    setFaceTrackingSensitivity(75);
    setFacePadding(20);
    
    // Reset Advanced settings
    setFrameImage(null);
    setFrameType('none');
    setFrameSize(10);
    setFrameColor('#000000');
    setLogoImage(null);
    setLogoPosition('bottom-right');
    setLogoSize(50);
    setLogoOpacity(80);
    setLayers(DEFAULT_LAYERS); // reset layers to default first
    
    const presetMCX = preset.manualCropX ?? 50;
    const presetMCY = preset.manualCropY ?? 50;
    setManualCropX(presetMCX);
    setManualCropY(presetMCY);

    if (preset.customization) {
      // Load canvasFormat if it exists
      if (preset.customization.canvasFormat) {
        setCanvasFormat(preset.customization.canvasFormat);
      } else {
        setCanvasFormat('9:16');
      }

      // Load face tracking settings
      if (preset.customization.faceTrackingEnabled) {
        setFaceTrackingEnabled(true);
        setFaceTrackingSubMode(preset.customization.faceTrackingSubMode || 'automatic');
        setFaceTrackingMode(preset.customization.faceTrackingMode || 'center');
        setFaceTrackingSensitivity(preset.customization.faceTrackingSensitivity || 75);
        setFacePadding(preset.customization.facePadding || 20);
      }
      
      // Load advanced settings if they exist
      if (preset.customization.frameImage !== undefined) {
        setFrameImage(preset.customization.frameImage);
        setFrameType(preset.customization.frameType || 'none');
        setFrameSize(preset.customization.frameSize || 10);
        setFrameColor(preset.customization.frameColor || '#000000');
        setLogoImage(preset.customization.logoImage);
        setLogoPosition(preset.customization.logoPosition || 'bottom-right');
        setLogoSize(preset.customization.logoSize || 50);
        setLogoOpacity(preset.customization.logoOpacity || 80);
      }

      // Load layers if they exist
      if (preset.customization.layers) {
        setLayers(preset.customization.layers);
      }
    }
    
    onUpdate({
      settings: {
        ...project.settings,
        formatSettingsEnabled: true,
        format: preset.format,
        aspectRatio: preset.aspectRatio,
        customWidth: preset.customWidth,
        customHeight: preset.customHeight,
        customization: preset.customization,
        manualCropX: presetMCX,
        manualCropY: presetMCY
      }
    });
  };

  const deletePreset = (presetId) => {
    const updated = savedPresets.filter(p => p.id !== presetId);
    setSavedPresets(updated);
    localStorage.setItem('formatPresets', JSON.stringify(updated));
  };

  const updateSettings = (newSettings) => {
    onUpdate({
      settings: {
      ...project.settings,
      ...newSettings
      }
    });
  };

  const getAspectRatio = () => {
    if (selectedFormat === 'custom') {
      return `${customWidth}/${customHeight}`;
    }
    if (selectedFormat === 'advanced') {
      if (canvasFormat === 'custom') {
        return `${customWidth}/${customHeight}`;
      }
      const format = formats.find(f => f.id === canvasFormat);
      return format ? format.ratio : '9/16';
    }
    const format = formats.find(f => f.id === selectedFormat);
    return format ? format.ratio : '16/9';
  };

  const getFormatLabel = () => {
    if (selectedFormat === 'advanced') {
      if (canvasFormat === 'custom') return 'Custom';
      const format = formats.find(f => f.id === canvasFormat);
      return format ? `${format.label} (Custom)` : 'Customize';
    }
    const format = formats.find(f => f.id === selectedFormat);
    return format ? format.label : 'Unknown';
  };

  const formatEnabled = project.settings?.formatSettingsEnabled ?? false;

  return (
    <Container>
      <Section>
        <SectionTitle>
          <Monitor size={16} />
          Format Selection
        </SectionTitle>
        
        <ToggleContainer style={{ marginBottom: '20px' }}>
          <ToggleSwitch
            active={formatEnabled}
            onClick={() => {
              onUpdate({
                settings: {
                  ...project.settings,
                  formatSettingsEnabled: !formatEnabled
                }
              });
            }}
          />
          <ToggleLabel style={{ fontWeight: 600 }}>
            {formatEnabled ? 'Enabled' : 'Disabled'}
          </ToggleLabel>
        </ToggleContainer>

        {formatEnabled && (
          <>
            <FormatGrid>
          {formats.map((format) => (
              <FormatOption
                key={format.id}
                selected={selectedFormat === format.id}
                onClick={() => handleFormatChange(format.id)}
              >
                <FormatIcon selected={selectedFormat === format.id}>
                <format.icon />
                </FormatIcon>
                <FormatLabel>{format.label}</FormatLabel>
              <FormatRatio>{format.description}</FormatRatio>
              </FormatOption>
          ))}
        </FormatGrid>

        {/* Custom Format Section */}
        {(selectedFormat === 'custom' || (selectedFormat === 'advanced' && canvasFormat === 'custom')) && (
          <CustomFormatSection>
            <RatioInputContainer>
              <RatioLabel>Width:</RatioLabel>
              <RatioInput
                type="number"
                value={customWidth}
                onChange={(e) => setCustomWidth(e.target.value)}
                onBlur={handleCustomRatioChange}
                min="1"
                max="999"
              />
              <RatioLabel>:</RatioLabel>
              <RatioLabel>Height:</RatioLabel>
              <RatioInput
                type="number"
                value={customHeight}
                onChange={(e) => setCustomHeight(e.target.value)}
                onBlur={handleCustomRatioChange}
                min="1"
                max="999"
              />
            </RatioInputContainer>
            
            <PresetContainer>
              <RatioLabel style={{ fontSize: '11px', marginRight: '8px' }}>Quick Presets:</RatioLabel>
              {presetRatios.map((preset) => (
                <PresetButton
                  key={preset.label}
                  onClick={() => handlePresetRatio(preset)}
                >
                  {preset.label}
                </PresetButton>
              ))}
            </PresetContainer>
          </CustomFormatSection>
        )}



        {/* Advanced Customization Section */}
        {formatEnabled && selectedFormat === 'advanced' && (
          <AdvancedSection>
            <SectionTitle>
              <Layers size={16} />
              Multi-Layer Canvas Composer
            </SectionTitle>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '8px', color: '#666' }}>
                Target Canvas Aspect Ratio:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '8px' }}>
                {formats.filter(f => f.id !== 'advanced').map((format) => (
                  <button
                    key={format.id}
                    onClick={() => {
                      setCanvasFormat(format.id);
                      updateCropSettings({ canvasFormat: format.id });
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px',
                      border: canvasFormat === format.id ? '2px solid #ff6b35' : '1px solid #ddd',
                      borderRadius: '6px',
                      background: canvasFormat === format.id ? '#ff6b3510' : '#f9f9f9',
                      color: canvasFormat === format.id ? '#ff6b35' : '#333',
                      fontWeight: 'bold',
                      fontSize: '11px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <format.icon size={12} />
                    {format.label === 'Shorts / Reels / TikTok' ? 'Shorts (9:16)' : format.label === 'YouTube / Landscape' ? 'YouTube (16:9)' : format.label === 'Instagram Square' ? 'Instagram (1:1)' : format.label === 'Instagram Portrait' ? 'Instagram (4:5)' : format.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Layers List and Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <ActionButton onClick={addTextLayer} variant="primary" style={{ flex: 1, justifyContent: 'center' }}>
                  <Plus size={14} /> Add Text Layer
                </ActionButton>
                <ActionButton onClick={addLogoLayer} variant="primary" style={{ flex: 1, justifyContent: 'center' }}>
                  <Plus size={14} /> Add Logo Layer
                </ActionButton>
              </div>
              
              <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '6px', padding: '6px', backgroundColor: '#f9f9f9' }}>
                {[...layers].sort((a,b) => b.zIndex - a.zIndex).map((layer) => {
                  const isBg = layer.id === 'bg';
                  const isVid = layer.id === 'vid';
                  const isActive = selectedLayerId === layer.id;
                  
                  return (
                    <LayerItemRow
                      key={layer.id}
                      active={isActive}
                      onClick={() => setSelectedLayerId(layer.id)}
                      style={{ opacity: layer.visible === false ? 0.6 : 1 }}
                    >
                      <LayerNameLabel>
                        {layer.type === 'background' && <Palette size={14} style={{ color: '#ec4899' }} />}
                        {layer.type === 'video' && <Video size={14} style={{ color: '#3b82f6' }} />}
                        {layer.type === 'text' && <Type size={14} style={{ color: '#10b981' }} />}
                        {layer.type === 'logo' && <Image size={14} style={{ color: '#f59e0b' }} />}
                        <span style={{ textTransform: 'capitalize' }}>
                          {layer.type} {layer.type === 'text' && `("${layer.content.substring(0, 10)}")`}
                        </span>
                      </LayerNameLabel>
                      
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <SmallButton
                          onClick={() => toggleLayerVisibility(layer.id)}
                          title={layer.visible === false ? "Show Layer" : "Hide Layer"}
                          style={{ color: layer.visible === false ? '#9ca3af' : undefined }}
                        >
                          {layer.visible === false ? <EyeOff size={10} /> : <Eye size={10} />}
                        </SmallButton>
                        <SmallButton
                          onClick={() => moveLayerDepth(layer.id, 'down')}
                          disabled={layer.zIndex === 0}
                          title="Move Down"
                        >
                          <ArrowDown size={10} />
                        </SmallButton>
                        <SmallButton
                          onClick={() => moveLayerDepth(layer.id, 'up')}
                          disabled={layer.zIndex === layers.length - 1}
                          title="Move Up"
                        >
                          <ArrowUp size={10} />
                        </SmallButton>
                        {!isBg && !isVid && (
                          <SmallButton
                            onClick={() => deleteLayer(layer.id)}
                            style={{ color: '#ef4444' }}
                            title="Delete Layer"
                          >
                            <Trash2 size={10} />
                          </SmallButton>
                        )}
                      </div>
                    </LayerItemRow>
                  );
                })}
              </div>
            </div>

            {/* Selected Layer Properties Editor */}
            {selectedLayerId && (() => {
              const layer = layers.find(l => l.id === selectedLayerId);
              if (!layer) return null;
              
              return (
                <CustomizeOption style={{ marginTop: '12px' }}>
                  <CustomizeTitle style={{ borderBottom: '1px solid #eee', paddingBottom: '6px', marginBottom: '12px', textTransform: 'capitalize' }}>
                    ⚙️ {layer.type} Properties
                  </CustomizeTitle>
                  
                  {layer.type === 'background' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Canvas Background Color</label>
                        <input
                          type="color"
                          value={layer.color || '#000000'}
                          onChange={(e) => updateSelectedLayerProperty('color', e.target.value)}
                          style={{ width: '100%', height: '36px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Custom Background Image</label>
                        <FileUploadButton onClick={() => document.getElementById('bgImageInput').click()}>
                          <Upload size={12} /> Upload Image
                        </FileUploadButton>
                        <input
                          id="bgImageInput"
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                updateSelectedLayerProperty('imagePath', event.target.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        {layer.imagePath && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                            <img src={layer.imagePath} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} alt="Background thumbnail" />
                            <SmallButton onClick={() => updateSelectedLayerProperty('imagePath', null)} style={{ color: '#ef4444' }}>
                              <X size={12} /> Clear
                            </SmallButton>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {layer.type === 'video' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                          <label style={{ fontSize: '10px', color: '#666' }}>X Position ({layer.x}%)</label>
                          <Slider
                            type="range"
                            min="-400"
                            max="400"
                            value={layer.x}
                            onChange={(e) => updateSelectedLayerProperty('x', parseInt(e.target.value))}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', color: '#666' }}>Y Position ({layer.y}%)</label>
                          <Slider
                            type="range"
                            min="-400"
                            max="400"
                            value={layer.y}
                            onChange={(e) => updateSelectedLayerProperty('y', parseInt(e.target.value))}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', color: '#666' }}>Width ({layer.w}%)</label>
                          <Slider
                            type="range"
                            min="10"
                            max="500"
                            value={layer.w}
                            onChange={(e) => updateSelectedLayerProperty('w', parseInt(e.target.value))}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', color: '#666' }}>Height ({layer.h}%)</label>
                          <Slider
                            type="range"
                            min="10"
                            max="500"
                            value={layer.h}
                            onChange={(e) => updateSelectedLayerProperty('h', parseInt(e.target.value))}
                          />
                        </div>
                      </div>

                      <ToggleContainer style={{ marginTop: '8px', marginBottom: '8px' }}>
                        <ToggleSwitch
                          active={layer.faceTracking}
                          onClick={() => updateSelectedLayerProperty('faceTracking', !layer.faceTracking)}
                        />
                        <ToggleLabel style={{ fontSize: '12px', fontWeight: 600 }}>
                          🎯 Auto Face Tracking Inside Layer
                        </ToggleLabel>
                      </ToggleContainer>

                      <ToggleContainer style={{ marginTop: '8px', marginBottom: '8px' }}>
                        <ToggleSwitch
                          active={layer.allowOverflow || false}
                          onClick={() => updateSelectedLayerProperty('allowOverflow', !layer.allowOverflow)}
                        />
                        <ToggleLabel style={{ fontSize: '12px', fontWeight: 600 }}>
                          ↔️ Allow Video Overflow (Show Full Frame)
                        </ToggleLabel>
                      </ToggleContainer>
                      

                    </div>
                  )}
                  
                  {layer.type === 'text' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Text Content</label>
                        <ModalInput
                          type="text"
                          value={layer.content}
                          onChange={(e) => updateSelectedLayerProperty('content', e.target.value)}
                          placeholder="Use {part_name} for part title"
                          style={{ marginBottom: '8px', padding: '8px' }}
                        />
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                          <label style={{ fontSize: '10px', color: '#666' }}>Font Size ({layer.style?.fontSize || 24}px)</label>
                          <Slider
                            type="range"
                            min="10"
                            max="120"
                            value={layer.style?.fontSize || 24}
                            onChange={(e) => updateSelectedLayerProperty('style.fontSize', parseInt(e.target.value))}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', color: '#666' }}>Font Color</label>
                          <input
                            type="color"
                            value={layer.style?.color || '#ffffff'}
                            onChange={(e) => updateSelectedLayerProperty('style.color', e.target.value)}
                            style={{ width: '100%', height: '24px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', color: '#666' }}>Font Family</label>
                          <select
                            value={layer.style?.fontFamily || 'Arial'}
                            onChange={(e) => updateSelectedLayerProperty('style.fontFamily', e.target.value)}
                            style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                          >
                            <option value="Arial">Arial</option>
                            <option value="Courier New">Courier New</option>
                            <option value="Georgia">Georgia</option>
                            <option value="Impact">Impact</option>
                            <option value="Times New Roman">Times New Roman</option>
                            <option value="Verdana">Verdana</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '10px', color: '#666' }}>Text Align</label>
                          <select
                            value={layer.style?.textAlign || 'center'}
                            onChange={(e) => updateSelectedLayerProperty('style.textAlign', e.target.value)}
                            style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                          >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {layer.type === 'logo' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Upload Image / Logo</label>
                        <FileUploadButton onClick={() => document.getElementById('logoLayerImageInput').click()}>
                          <Upload size={12} /> Upload Image
                        </FileUploadButton>
                        <input
                          id="logoLayerImageInput"
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                updateSelectedLayerProperty('imagePath', event.target.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        {layer.imagePath && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                            <img src={layer.imagePath} style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #eee' }} alt="Logo thumbnail" />
                            <SmallButton onClick={() => updateSelectedLayerProperty('imagePath', null)} style={{ color: '#ef4444' }}>
                              <X size={12} /> Clear
                            </SmallButton>
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: '#666' }}>Opacity ({layer.opacity || 80}%)</label>
                        <Slider
                          type="range"
                          min="10"
                          max="100"
                          value={layer.opacity || 80}
                          onChange={(e) => updateSelectedLayerProperty('opacity', parseInt(e.target.value))}
                        />
                      </div>
                    </div>
                  )}
                </CustomizeOption>
              );
            })()}
          </AdvancedSection>
        )}

        {/* Preview */}
        <PreviewContainer>
          <PreviewTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Eye size={16} />
              Live Preview - {getFormatLabel()} ({getAspectRatio()})
            </span>
            {formatEnabled && (
              <span style={{ fontSize: '10px', color: '#888' }}>
                💡 Click & Drag layers to position/resize
              </span>
            )}
          </PreviewTitle>
          
          {formatEnabled && selectedFormat === 'advanced' ? (
            <CanvasContainer id="canvas-preview-frame" ratio={getAspectRatio()} onClick={() => setSelectedLayerId(null)}>
              {layers.filter(l => l.visible !== false).map((layer) => {
                const isSelected = selectedLayerId === layer.id;
                return (
                  <LayerWrapper
                    key={layer.id}
                    active={isSelected}
                    y={layer.y}
                    x={layer.x}
                    w={layer.w}
                    h={layer.h}
                    zIndex={layer.zIndex}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLayerId(layer.id);
                    }}
                    onMouseDown={(e) => handleLayerMouseDown(e, layer.id)}
                  >
                    {/* Render content based on layer type */}
                    {layer.type === 'background' && (
                      layer.imagePath ? (
                        <img src={layer.imagePath} style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} alt="Background" />
                      ) : (
                        <div style={{ width: '100%', height: '100%', backgroundColor: layer.color || '#000000' }} />
                      )
                    )}
                    
                    {layer.type === 'video' && (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        gap: '4px',
                        padding: '8px',
                        textAlign: 'center'
                      }}>
                        <Video size={20} />
                        <span style={{ fontSize: '10px', fontWeight: 'bold' }}>Video Layer</span>
                        <span style={{ fontSize: '8px', opacity: 0.8 }}>
                          {layer.faceTracking ? '🎯 Auto Tracked' : `Manual crop`}
                        </span>
                      </div>
                    )}
                    
                    {layer.type === 'text' && (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: layer.style?.textAlign || 'center',
                        padding: '4px',
                        boxSizing: 'border-box'
                      }}>
                        <span style={{
                          fontSize: `${(layer.style?.fontSize || 24) * 0.4}px`, // Scale down text for preview size
                          color: layer.style?.color || '#ffffff',
                          fontFamily: layer.style?.fontFamily || 'Arial',
                          fontWeight: layer.style?.fontWeight || 'bold',
                          textAlign: layer.style?.textAlign || 'center',
                          width: '100%',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal'
                        }}>
                          {layer.content.replace('{part_name}', project.name || 'Sample Part')}
                        </span>
                      </div>
                    )}
                    
                    {layer.type === 'logo' && (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255, 255, 255, 0.05)',
                        opacity: (layer.opacity || 100) / 100
                      }}>
                        {layer.imagePath ? (
                          <img src={layer.imagePath} style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} alt="Logo" />
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: '#888', gap: '2px' }}>
                            <Image size={16} />
                            <span style={{ fontSize: '8px' }}>Logo</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Corner resizing handles */}
                    {isSelected && layer.id !== 'bg' && (
                      <>
                        <ResizeHandle position="top-left" onMouseDown={(e) => handleResizeMouseDown(e, layer.id, 'top-left')} />
                        <ResizeHandle position="top-right" onMouseDown={(e) => handleResizeMouseDown(e, layer.id, 'top-right')} />
                        <ResizeHandle position="bottom-left" onMouseDown={(e) => handleResizeMouseDown(e, layer.id, 'bottom-left')} />
                        <ResizeHandle position="bottom-right" onMouseDown={(e) => handleResizeMouseDown(e, layer.id, 'bottom-right')} />
                      </>
                    )}
                  </LayerWrapper>
                );
              })}
            </CanvasContainer>
          ) : (
            <PreviewFrame ratio={getAspectRatio()}>
              {frameType !== 'none' && (
                <FrameOverlay
                  frameType={frameType}
                  frameSize={frameSize}
                  frameColor={frameColor}
                />
              )}
              Video Preview
              {getFormatLabel() === 'Custom' && (
                <div style={{ fontSize: '10px', marginTop: '4px' }}>
                  {customWidth}:{customHeight}
                </div>
              )}
              {faceTrackingEnabled && (
                <div style={{
                  position: 'absolute',
                  top: '20%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '40px',
                  height: '40px',
                  border: '2px solid #ff6b35',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 107, 53, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  animation: 'facePulse 2s infinite'
                }}>
                  🎯
                </div>
              )}
            </PreviewFrame>
          )}
        </PreviewContainer>
          </>
        )}
      </Section>

      {formatEnabled && (
        <>

      {/* Face Tracker Section */}
      <Section>
        <SectionTitle>
          <Target size={16} />
          🎯 Face Tracker
        </SectionTitle>
        
        <ToggleContainer style={{ marginBottom: '16px' }}>
          <ToggleSwitch
            active={faceTrackingEnabled}
            onClick={() => {
              const val = !faceTrackingEnabled;
              setFaceTrackingEnabled(val);
              updateCropSettings({ faceTrackingEnabled: val });
            }}
          />
          <ToggleLabel style={{ fontWeight: 600 }}>
            {faceTrackingEnabled ? 'Enabled' : 'Disabled'}
          </ToggleLabel>
        </ToggleContainer>

        {faceTrackingEnabled && (
          <CustomizeGrid>
            <CustomizeOption style={{ gridColumn: 'span 2', marginBottom: '8px' }}>
              <CustomizeTitle style={{ marginBottom: '12px' }}>
                Tracking Offset Mode
              </CustomizeTitle>
              <div style={{ display: 'flex', gap: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                  <input
                    type="radio"
                    name="faceTrackingSubMode"
                    value="automatic"
                    checked={faceTrackingSubMode === 'automatic'}
                    onChange={() => {
                      setFaceTrackingSubMode('automatic');
                      updateCropSettings({ faceTrackingSubMode: 'automatic' });
                    }}
                  />
                  🤖 Fully Automatic Center
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' }}>
                  <input
                    type="radio"
                    name="faceTrackingSubMode"
                    value="manual"
                    checked={faceTrackingSubMode === 'manual'}
                    onChange={() => {
                      setFaceTrackingSubMode('manual');
                      updateCropSettings({ faceTrackingSubMode: 'manual' });
                    }}
                  />
                  ✏️ Manual Nudge Fine-Tuning
                </label>
              </div>
            </CustomizeOption>

            {faceTrackingSubMode === 'automatic' && (
              <>
                <CustomizeOption>
                  <CustomizeTitle>
                    Detection Mode
                  </CustomizeTitle>
                  
                  <select
                    value={faceTrackingMode}
                    onChange={(e) => {
                      setFaceTrackingMode(e.target.value);
                      updateCropSettings({ faceTrackingMode: e.target.value });
                    }}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <option value="center">🎯 Center on Face</option>
                    <option value="follow">🔄 Follow Face Movement</option>
                    <option value="smart">🧠 Smart Detection</option>
                  </select>
                  
                  <div style={{ fontSize: '10px', marginTop: '4px', color: '#666' }}>
                    {faceTrackingMode === 'center' && 'Keeps faces centered in frame'}
                    {faceTrackingMode === 'follow' && 'Tracks face movement dynamically'}
                    {faceTrackingMode === 'smart' && 'Auto-selects best positioning'}
                  </div>
                </CustomizeOption>

                <CustomizeOption>
                  <CustomizeTitle>
                    Detection Settings
                  </CustomizeTitle>
                  
                  <SliderContainer>
                    <SliderLabel>
                      <span>Sensitivity</span>
                      <span>{faceTrackingSensitivity}%</span>
                    </SliderLabel>
                    <Slider
                      type="range"
                      min="10"
                      max="100"
                      value={faceTrackingSensitivity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setFaceTrackingSensitivity(val);
                        updateCropSettings({ faceTrackingSensitivity: val });
                      }}
                    />
                  </SliderContainer>

                  <SliderContainer>
                    <SliderLabel>
                      <span>Face Padding</span>
                      <span>{facePadding}%</span>
                    </SliderLabel>
                    <Slider
                      type="range"
                      min="5"
                      max="50"
                      value={facePadding}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setFacePadding(val);
                        updateCropSettings({ facePadding: val });
                      }}
                    />
                  </SliderContainer>
                </CustomizeOption>
              </>
            )}
          </CustomizeGrid>
        )}

        {faceTrackingEnabled && faceTrackingSubMode === 'automatic' && (
          <div style={{ 
            fontSize: '10px', 
            color: '#666', 
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '6px',
            marginTop: '12px',
            border: '1px solid #e9ecef'
          }}>
            💡 <strong>Face Tracker Tips:</strong><br/>
            • <strong>Center:</strong> Best for Shorts & vertical videos<br/>
            • <strong>Follow:</strong> Great for moving speakers<br/>
            • <strong>Smart:</strong> Auto-adapts to video content<br/>
            • <strong>Sensitivity 70-80%:</strong> Optimal for most videos<br/>
            • <strong>Face Padding 15-25%:</strong> Recommended frame space
          </div>
        )}

        {faceTrackingEnabled && faceTrackingSubMode === 'manual' && (
          <div style={{ 
            fontSize: '11px', 
            color: '#666', 
            backgroundColor: '#fffbeb',
            padding: '12px',
            borderRadius: '6px',
            marginTop: '12px',
            border: '1px solid #fef3c7'
          }}>
            💡 <strong>Manual Nudge Mode:</strong> Use the <strong>📏 Crop Alignment</strong> sliders below to fine-tune the camera's focus position. The face tracker will dynamically center on faces, and apply your nudge/offset relative to those detected faces.
          </div>
        )}
      </Section>

      {/* Crop Alignment Section */}
      {selectedFormat !== 'original' && (!faceTrackingEnabled || faceTrackingSubMode === 'manual') && (
        <Section>
          <SectionTitle>
            <Sliders size={16} />
            📏 Crop Alignment
          </SectionTitle>
          
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
            {faceTrackingEnabled 
              ? "💡 Face tracking is enabled. The slider acts as a relative nudge/offset to fine-tune the camera's focus."
              : "💡 Adjust the slider to set the static position of the cropped layout."
            }
          </div>

          {(() => {
            const ratioStr = getAspectRatio();
            let ratioVal = 1.77;
            if (ratioStr) {
              const parts = ratioStr.split('/');
              if (parts.length === 2) {
                const w = parseFloat(parts[0]);
                const h = parseFloat(parts[1]);
                if (w > 0 && h > 0) ratioVal = w / h;
              }
            }
            
            if (ratioVal <= 1.05) {
              return (
                <SliderContainer>
                  <SliderLabel>
                    <span>{faceTrackingEnabled ? 'Horizontal Nudge (Left - Right)' : 'Horizontal Crop Position'}</span>
                    <span>{manualCropX === 50 ? 'Center (50%)' : `${manualCropX}%`}</span>
                  </SliderLabel>
                  <Slider
                    type="range"
                    min="0"
                    max="100"
                    value={manualCropX}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      setManualCropX(val);
                      updateCropSettings({ manualCropX: val });
                    }}
                  />
                </SliderContainer>
              );
            } else {
              return (
                <SliderContainer>
                  <SliderLabel>
                    <span>{faceTrackingEnabled ? 'Vertical Nudge (Top - Bottom)' : 'Vertical Crop Position'}</span>
                    <span>{manualCropY === 50 ? 'Center (50%)' : `${manualCropY}%`}</span>
                  </SliderLabel>
                  <Slider
                    type="range"
                    min="0"
                    max="100"
                    value={manualCropY}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      setManualCropY(val);
                      updateCropSettings({ manualCropY: val });
                    }}
                  />
                </SliderContainer>
              );
            }
          })()}
        </Section>
      )}
        </>
      )}

      {/* Preset Management */}
      <Section>
        <SectionTitle>
          <Save size={16} />
          Format Presets
        </SectionTitle>
        
        <PresetActions>
          <ActionButton
            variant="primary"
            onClick={() => setShowSaveModal(true)}
          >
            <Save size={14} />
            Save Current Format
          </ActionButton>
          
          <ActionButton onClick={() => document.getElementById('presetUpload').click()}>
            <Upload size={14} />
            Import Presets
          </ActionButton>
          
          {savedPresets.length > 0 && (
            <ActionButton onClick={() => {
              const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(savedPresets, null, 2));
              const downloadAnchorNode = document.createElement('a');
              downloadAnchorNode.setAttribute("href", dataStr);
              downloadAnchorNode.setAttribute("download", "format_presets.json");
              document.body.appendChild(downloadAnchorNode);
              downloadAnchorNode.click();
              downloadAnchorNode.remove();
            }}>
              <Download size={14} />
              Export Presets
            </ActionButton>
          )}
        </PresetActions>

        {savedPresets.length > 0 && (
          <SavedPresetsList>
            {savedPresets.map((preset) => (
              <PresetItem key={preset.id}>
                <PresetName>{preset.name}</PresetName>
                <PresetInfo>
                  {preset.format === 'custom' 
                    ? `${preset.customWidth}:${preset.customHeight}` 
                    : preset.format
                  }
                  {preset.customization?.faceTrackingEnabled && (
                    <span style={{ 
                      marginLeft: '4px', 
                      fontSize: '10px', 
                      backgroundColor: '#ff6b35', 
                      color: 'white', 
                      padding: '1px 4px', 
                      borderRadius: '2px' 
                    }}>
                      🎯
                    </span>
                  )}
                </PresetInfo>
                <PresetActions2>
                  <SmallButton
                    onClick={() => loadPreset(preset)}
                    title="Apply this preset"
                  >
                    <Copy size={12} />
                  </SmallButton>
                  <SmallButton
                    onClick={() => deletePreset(preset.id)}
                    title="Delete preset"
                  >
                    <X size={12} />
                  </SmallButton>
                </PresetActions2>
              </PresetItem>
            ))}
          </SavedPresetsList>
        )}
      </Section>

      {/* Hidden file inputs */}
      <input
        id="frameUpload"
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFrameUpload}
      />
      <input
        id="logoUpload"
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleLogoUpload}
      />
      <input
        id="presetUpload"
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              try {
                const imported = JSON.parse(event.target.result);
                setSavedPresets([...savedPresets, ...imported]);
                localStorage.setItem('formatPresets', JSON.stringify([...savedPresets, ...imported]));
              } catch (error) {
                console.error('Error importing presets:', error);
              }
            };
            reader.readAsText(file);
          }
        }}
      />

      {/* Save Preset Modal */}
      {showSaveModal && (
        <Modal onClick={() => setShowSaveModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Save Format Preset</ModalTitle>
            <ModalInput
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Enter preset name..."
              onKeyPress={(e) => e.key === 'Enter' && savePreset()}
              autoFocus
            />
            <ModalActions>
              <ActionButton onClick={() => setShowSaveModal(false)}>
                Cancel
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={savePreset}
                disabled={!presetName.trim()}
              >
                <Save size={14} />
                Save Preset
              </ActionButton>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default FormatCropping;